import { useEffect, useRef } from "react";
import { useAppSelector } from "../../store/hooks";
import { selectCurrentUser } from "../../store/selectors/authSelectors";
import usePopup from "./usePopup";
import ContactInfoValidationPopup from "../../popups/ContactInfoValidationPopup";
import { shouldShowContactInfoGate } from "./contactInfoGateRule";
import { useCurrentUserProfile } from "./useCurrentUserProfile";

/**
 * Mounted ONCE (in PageWrapper) so the "Personal contact info" popup is created a single time,
 * regardless of how many LangSelect/Header/Sidebar/Footer instances render.
 *
 * spec-42 — this hook used to mix TWO sources of truth that update at different times: `userId`/`role`
 * from Redux (which changes the instant an account switch starts) and `clientId`/`contactInfoConfirmed`
 * from RTK Query's `data` (which lags, and across an arg change falls back to the PREVIOUS user's row).
 * Switching away from an excluded-role account therefore opened this popup for the new user holding the
 * old user's details, and Approve PUT them to the old user's id — a 401 there, and silent cross-account
 * writes in LangSelect/UserSettings. Everything identity-scoped now comes from useCurrentUserProfile,
 * so `profile`, `userId` and `role` are guaranteed to describe the SAME user. That is the entire fix.
 */
const useContactInfoValidationGate = () => {
  const user = useAppSelector(selectCurrentUser);
  const { profile } = useCurrentUserProfile();
  const { createPopup } = usePopup();

  // D6 — keyed to the user it fired for, not to the mount. PageWrapper (App.tsx) wraps the whole app
  // INCLUDING the login route and never unmounts, so a plain boolean could never re-arm for the next
  // account in the same tab: once shown to anyone, the gate would be dead for every later login here.
  const openedForRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!profile || openedForRef.current === user?.userId) return;

    const show = shouldShowContactInfoGate({
      clientId: profile.ClientId,
      userId: user?.userId,
      // Read the flag from the FRESH server response, not the Redux session. On reload, App.tsx
      // rebuilds the session from the stale localStorage token and refreshes only `culture`, so
      // `user.ContactInfoConfirmed` can lag the DB (e.g. token holds true while the row is now false)
      // and the popup would never appear. Spec 10 / commit 41defe2d — still the right call.
      //
      // What makes it SAFE is that `profile` comes from `currentData` + an `Id === userId` check
      // (useCurrentUserProfile), so "fresh server data" can no longer mean "a different user's fresh
      // server data". Do not swap this back to a plain `useGetUserByIdQuery(...).data`.
      contactInfoConfirmed: profile.ContactInfoConfirmed,
      role: user?.role,
    });

    if (show) {
      openedForRef.current = user?.userId;
      createPopup(ContactInfoValidationPopup(profile));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId, profile]);
};

export default useContactInfoValidationGate;
